import { Spinner } from "@shared/components";
import { useAllBonuses } from "@shared/components/Modal/info/api/get-all-bonuses";
import { BonusTable } from "@shared/components/Modal/info/bonus-table";
import { useLobbyParams } from "@shared/hooks/lobby";
import { $f } from "@shared/hooks/shared";
import { useIsAuth, useUser, useUserBankGroup } from "@shared/hooks/user";
import { Sweepstakes } from "@shared/modules/sweepstakes";
import { root } from "@shared/utils";
import { determinateCurrency } from "@shared/utils/sweepstakes";
import React, { memo } from "react";
import { useIntl } from "react-intl";
import Modal from "react-modal";

import { NoJpWagerGames } from "./no-jp-wager-games";

interface IContactsModalProps {
  onClose?: () => void;
  isOpen: boolean;
}

const enum BonusType {
  cashback = "cashback",
  bounceback = "bounceback",
  lastchance = "lastchance",
}

export const ModalInfo: React.FC<IContactsModalProps> = memo(
  ({ isOpen, onClose }) => {
    const intl = useIntl();
    const [isOpenModal, setIsOpenModal] = React.useState<boolean>(isOpen);
    const { bonuses, wager_coeff, jackpots } = useUserBankGroup();
    const isAuth = useIsAuth();
    const user = useUser();
    const { sweepstakes: isSweepstakesEnabled } = useLobbyParams() ?? {};
    const { acceptedBonus, isPending } = useAllBonuses();

    const realBalance = $f.formatMoney($f.toMoney(user.balance));
    const currency = user.currency;

    const getHappyHours = (
      startTime: string,
      endTime: string,
      boostPercent: number
    ) => {
      return (
        <div className="modal-info__happy-hours">
          <div className="modal-info__happy-hours-title-wrapper">
            <div className="modal-info__happy-hours-title">
              {intl.formatMessage({
                id: "boilerplate.components.InfoModal.HappyHours.Title",
              })}
              :
            </div>
            <div className="modal-info__happy-hours-text">
              {intl.formatMessage(
                {
                  id: "boilerplate.components.InfoModal.HappyHours.TimeRangeText",
                },
                { startTime, endTime }
              )}
            </div>
          </div>
          <div className="modal-info__happy-hours-text">
            {intl.formatMessage({
              id: "boilerplate.components.InfoModal.HappyHours.Text1",
            })}
            <span className="modal-info__extra-value"> {boostPercent}%</span>
          </div>
        </div>
      );
    };

    const getWagerCoeff = (type: BonusType) => {
      if (hasWager(type)) {
        return (
          <div className="modal-info__wager-coeff">
            <div className="modal-info__wager-coeff-title">
              {intl.formatMessage({
                id: "boilerplate.components.InfoModal.WagerCoeff.Title",
              })}
              :
            </div>
            <div className="modal-info__wager-coeff-text">
              <span className="modal-info__extra-value">{wager_coeff}</span>
            </div>
          </div>
        );
      }
    };

    const bonusTableLayout = () => {
      if (!acceptedBonus) {
        return;
      }

      return (
        <>
          <div style={{ display: "flex" }}>
            <div className="modal-info__balance-title">
              {intl.formatMessage({
                id: "boilerplate.components.InfoModal.RemainingWager.Title",
              })}
              :
            </div>
            <div className="modal-info__balance-value">
              {$f.formatMoney($f.toMoney(acceptedBonus?.remaining_wager))}{" "}
              {currency}
            </div>
          </div>
          <div style={{ display: "flex" }}>
            <div className="modal-info__balance-title">
              {intl.formatMessage({ id: "wager" })}:
            </div>
            <div className="modal-info__balance-value">
              {$f.formatMoney($f.toMoney(acceptedBonus?.wager))} {currency}
            </div>
          </div>
        </>
      );
    };

    const getBonusLayout = () => {
      if (isPending) {
        return (
          <div style={{ position: "relative", marginTop: "1dvh" }}>
            <Spinner />
          </div>
        );
      }

      let ComponentBalance = Sweepstakes;

      if (!isSweepstakesEnabled) {
        // eslint-disable-next-line react/display-name
        ComponentBalance = () => (
          <div style={{ display: "flex" }}>
            <div className="modal-info__balance-title">
              {intl.formatMessage({
                id: "boilerplate.components.Popup.Balance",
              })}
              :
            </div>
            <div className="modal-info__balance-value">
              {realBalance} {currency}
            </div>
          </div>
        );
      }

      return (
        <>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <ComponentBalance />
            {bonusTableLayout()}
          </div>
          <h1 className="modal-info__jackpots-title">
            {intl.formatMessage({ id: "bonuses" })}
          </h1>
          <div style={{ overflowX: "auto" }}>
            <BonusTable />
          </div>
        </>
      );
    };

    const onCloseModal = () => {
      setIsOpenModal(false);
      onClose && onClose();
    };

    const hasWager = (type: BonusType) => bonuses[type]?.wager ?? false;

    const bonusValuePercent = (type: BonusType) => bonuses[type]?.value ?? 0;

    const isHappyHours = (type: BonusType) =>
      bonuses[type]?.happyHours ?? false;

    const bonusCooldown = (type: BonusType) => bonuses[type]?.cooldown || 0;

    const bonusHappyHoursBoostPercent = (type: BonusType) =>
      bonuses[type]?.happyHours?.boost || 0;

    const bonusHappyHoursRange = (type: BonusType) => {
      const timeZoneOffsetHours = new Date().getTimezoneOffset() / 60 || 0;

      const from = new Date(bonuses[type]?.happyHours?.from) ?? new Date();
      const to = new Date(bonuses[type]?.happyHours?.to) ?? new Date();

      from.setHours(from.getHours() - timeZoneOffsetHours);
      to.setHours(to.getHours() - timeZoneOffsetHours);

      return {
        fromHM: from.toLocaleTimeString(),
        toHM: to.toLocaleTimeString(),
      };
    };

    const bonusThreshold = (type: BonusType) => {
      const { threshold } = bonuses[type];
      return isNaN(threshold / 100) ? 0 : threshold / 100;
    };

    const allowBonusTypes = () => {
      const bonusesAllow = [];

      if (bonuses?.cashback) {
        bonusesAllow.push(BonusType.cashback);
      }

      if (bonuses?.bounceback) {
        bonusesAllow.push(BonusType.bounceback);
      }

      if (bonuses?.lastchance) {
        bonusesAllow.push(BonusType.lastchance);
      }

      return bonusesAllow;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ModalComponent = Modal as any;

    return (
      <ModalComponent
        isOpen={isOpenModal && isAuth}
        contentLabel="Info Modal"
        overlayClassName="modal-info__overlay"
        className="modal-info__content"
        appElement={root as HTMLElement}
        onRequestClose={onCloseModal}
      >
        <h1 className="modal-info__title">
          {" "}
          {intl.formatMessage({
            id: "boilerplate.info",
          })}
        </h1>
        <div className="modal-info__height-wrapper">
          <div style={{ position: "relative" }}>{getBonusLayout()}</div>
          {!allowBonusTypes().length && !jackpots?.enabled && (
            <div className="modal-info__bonuses-not-found">
              <div className="modal-info__bonuses-not-found-title">
                {intl.formatMessage({
                  id: "boilerplate.components.InfoModal.BonusNotFound.Title",
                })}
              </div>
            </div>
          )}
          {jackpots?.enabled && (
            <div className="modal-info__jackpots">
              <div className="modal-info__jackpots-title">
                {intl.formatMessage({
                  id: "boilerplate.components.InfoModal.JackpotTitle",
                })}
              </div>
              <div className="modal-info__jackpots-text">
                {intl.formatMessage({
                  id: "boilerplate.components.InfoModal.JackpotText1",
                })}
              </div>
              <div className="modal-info__jackpots-text">
                {intl.formatMessage({
                  id: "boilerplate.components.InfoModal.JackpotText2",
                })}
              </div>
              <div className="modal-info__jackpots-text">
                {intl.formatMessage({
                  id: "boilerplate.components.InfoModal.JackpotText3",
                })}
              </div>
            </div>
          )}
          {allowBonusTypes().length > 0 && (
            <div className="modal-info__bonuses">
              <div className="modal-info__jackpots-title">
                {" "}
                {intl.formatMessage({
                  id: "boilerplate.components.InfoModal.BonusesRules.Title",
                })}
              </div>
              <div className="modal-info__jackpots-text">
                -{" "}
                {intl.formatMessage({
                  id: "boilerplate.components.InfoModal.BonusesRules.Text1",
                })}
              </div>
              <div className="modal-info__jackpots-text">
                -{" "}
                {intl.formatMessage({
                  id: "boilerplate.components.InfoModal.BonusesRules.Text2",
                })}
              </div>
              <div className="modal-info__jackpots-text">
                -{" "}
                {intl.formatMessage({
                  id: "boilerplate.components.InfoModal.BonusesRules.Text3",
                })}
              </div>
              <div className="modal-info__jackpots-text">
                -{" "}
                {intl.formatMessage({
                  id: "boilerplate.components.InfoModal.BonusesRules.Text4",
                })}
              </div>
              <div className="modal-info__jackpots-text">
                <br />
                <b>
                  {intl.formatMessage({
                    id: "boilerplate.components.InfoModal.Note",
                  })}
                  :
                </b>

                <div>
                  {intl.formatMessage({
                    id: "boilerplate.components.InfoModal.Note.Text",
                  })}
                </div>
              </div>
            </div>
          )}
          {allowBonusTypes().length > 0 && !isSweepstakesEnabled && (
            <div className="modal-info__cancellation-bonuses">
              <div className="modal-info__cancellation-bonuses-title">
                {intl.formatMessage({
                  id: "boilerplate.components.InfoModal.AutoCancelBonus.Title",
                })}
              </div>
              <div className="modal-info__cancellation-bonuses-text">
                {intl.formatMessage({
                  id: "boilerplate.components.InfoModal.AutoCancelBonus.Text1",
                })}
              </div>
              <div className="modal-info__cancellation-bonuses-text">
                {intl.formatMessage({
                  id: "boilerplate.components.InfoModal.AutoCancelBonus.Text2",
                })}
              </div>
            </div>
          )}
          {allowBonusTypes().includes(BonusType.cashback) && (
            <div className="modal-info__cashback">
              <div className="modal-info__cashback-title">Cashback</div>

              <div className="modal-info__cashback-text">
                {intl.formatMessage({
                  id: "boilerplate.components.InfoModal.Cashback.Text1",
                })}
                <span className="modal-info__extra-value">
                  {" "}
                  {bonusValuePercent(BonusType.cashback)}%{" "}
                </span>
                {intl.formatMessage({
                  id: "boilerplate.components.InfoModal.Cashback.Text2",
                })}
              </div>

              <div className="modal-info__cashback-text">
                {intl.formatMessage({
                  id: "boilerplate.components.InfoModal.Cashback.Text3",
                })}
                <span className="modal-info__extra-value">
                  {" "}
                  {bonusThreshold(BonusType.cashback)}{" "}
                  {determinateCurrency(isSweepstakesEnabled, currency)}
                </span>
              </div>
              {getWagerCoeff(BonusType.cashback)}
              {isHappyHours(BonusType.cashback) &&
                getHappyHours(
                  bonusHappyHoursRange(BonusType.cashback).fromHM,
                  bonusHappyHoursRange(BonusType.cashback).toHM,
                  bonusHappyHoursBoostPercent(BonusType.cashback)
                )}
              {bonusCooldown(BonusType.cashback) !== 0 && (
                <div className="modal-info__cashback-text">
                  {intl.formatMessage({
                    id: "boilerplate.components.InfoModal.Cashback.Text4",
                  })}
                  <span className="modal-info__extra-value">
                    {" "}
                    {bonusCooldown(BonusType.cashback)}
                  </span>
                  <span>
                    {" "}
                    {intl.formatMessage({
                      id: "boilerplate.hours",
                    })}{" "}
                  </span>
                </div>
              )}
            </div>
          )}
          {allowBonusTypes().includes(BonusType.bounceback) && (
            <div className="modal-info__bounceback">
              <div className="modal-info__bounceback-title"> Bounceback</div>
              <div className="modal-info__bounceback-text">
                {intl.formatMessage({
                  id: "boilerplate.components.InfoModal.Bounceback.Text1",
                })}{" "}
                <span className="modal-info__extra-value">
                  {bonusValuePercent(BonusType.bounceback)}%
                </span>
                <span>
                  {" "}
                  {intl.formatMessage({
                    id: "boilerplate.components.InfoModal.Bounceback.Text2",
                  })}
                </span>
              </div>
              <div className="modal-info__bounceback-text">
                {intl.formatMessage({
                  id: "boilerplate.components.InfoModal.Bounceback.Text3",
                })}
              </div>
              {getWagerCoeff(BonusType.bounceback)}
              {bonusCooldown(BonusType.bounceback) !== 0 && (
                <div className="modal-info__bounceback-text">
                  {intl.formatMessage({
                    id: "boilerplate.components.InfoModal.Bounceback.Text4",
                  })}
                  <span className="modal-info__extra-value">
                    {" "}
                    {bonusCooldown(BonusType.bounceback)}
                  </span>
                  <span>
                    {" "}
                    {intl.formatMessage({
                      id: "boilerplate.hours",
                    })}{" "}
                  </span>
                </div>
              )}
              {isHappyHours(BonusType.bounceback) &&
                getHappyHours(
                  bonusHappyHoursRange(BonusType.bounceback).fromHM,
                  bonusHappyHoursRange(BonusType.bounceback).toHM,
                  bonusHappyHoursBoostPercent(BonusType.bounceback)
                )}
            </div>
          )}
          {allowBonusTypes().includes(BonusType.lastchance) && (
            <div className="modal-info__last-chance">
              <div className="modal-info__last-chance-title"> Last Chance</div>

              <div className="modal-info__last-chance-text">
                {intl.formatMessage({
                  id: "boilerplate.components.InfoModal.LastChance.Text1",
                })}{" "}
                <span className="modal-info__extra-value">
                  {bonusValuePercent(BonusType.lastchance)}%{" "}
                </span>
                {intl.formatMessage({
                  id: "boilerplate.components.InfoModal.LastChance.Text2",
                })}
                <div className="modal-info__last-chance-text">
                  {intl.formatMessage({
                    id: "boilerplate.components.InfoModal.LastChance.Text3",
                  })}
                  <span className="modal-info__extra-value">
                    {" "}
                    {bonusThreshold(BonusType.lastchance)}{" "}
                    {determinateCurrency(isSweepstakesEnabled, currency)}
                  </span>
                </div>
              </div>
              {getWagerCoeff(BonusType.lastchance)}
              {bonusCooldown(BonusType.lastchance) !== 0 && (
                <div className="modal-info__last-chance-text">
                  {intl.formatMessage({
                    id: "boilerplate.components.InfoModal.LastChance.Text4",
                  })}
                  <span className="modal-info__extra-value">
                    {" "}
                    {bonusCooldown(BonusType.lastchance)}
                  </span>
                  <span>
                    {" "}
                    {intl.formatMessage({
                      id: "boilerplate.hours",
                    })}
                  </span>
                </div>
              )}
            </div>
          )}
          <NoJpWagerGames />
        </div>
        <div
          className="modal-info__button btn btn--secondary btn--lg"
          onClick={onCloseModal}
        >
          {intl.formatMessage({
            id: "boilerplate.components.GameMessage.Link",
          })}
        </div>
        <div
          className="modal-info__close ico ico-close"
          onClick={onCloseModal}
        />
      </ModalComponent>
    );
  }
);

ModalInfo.displayName = "InfoModal";
